"use client";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components-shared/ui/pagination";

import { useRouter, useSearchParams } from "next/navigation";

/**
 * PropsPagination component displays a pagination component for a list of props.
 * It takes in the current page, total number of pages, a function to set the page,
 * and flags for whether the current page is the first or last page.
 * It returns a Pagination component with the appropriate links and ellipses.
 */
const PropsPagination = ({
  page: current = 1,
  pagesCount = 1,
  setPage,
}: {
  page?: number;
  pagesCount?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const router = useRouter();
  const searchParams = new URLSearchParams(useSearchParams());

  const addQueryParam = (key: string, value: number) => {
    if (setPage) {
      setPage(value);
    } else {
      searchParams.set(key, value.toString());
      router.push(`?${searchParams.toString()}`);
    }
  };

  const handlePageChange = addQueryParam.bind(null, "page");

  return (
    // Renders the Pagination component
    <Pagination className="flex justify-end mt-4 col-span-full">
      <PaginationContent>
        {/* Previous page link */}
        <PaginationPrevious
          disabled={current === 1}
          onClick={() => handlePageChange(current - 1)}
        />
        {/* First page link */}
        {current > 2 && (
          <>
            <PaginationLink
              key={1}
              isActive={current === 1}
              onClick={() => handlePageChange(1)}
            >
              1
            </PaginationLink>
            {/* Ellipsis for showing more pages */}
            {current > 3 && <PaginationEllipsis />}
          </>
        )}
        {/* Links for pages in the range of current - 1 to current + 1 */}
        {Array.from({
          length:
            Math.min(current + 1, pagesCount) - Math.max(1, current - 1) + 1,
        }).map((_, i) => {
          const page = Math.max(1, current - 1) + i;
          return (
            <PaginationLink
              key={page}
              isActive={current === page}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </PaginationLink>
          );
        })}
        {/* Ellipsis for showing more pages */}
        {current < pagesCount - 1 && (
          <>
            {current < pagesCount - 2 && <PaginationEllipsis />}
            {/* Last page link */}
            <PaginationLink
              key={pagesCount}
              isActive={current === pagesCount}
              onClick={() => handlePageChange(pagesCount)}
            >
              {pagesCount}
            </PaginationLink>
          </>
        )}
        {/* Next page link */}
        <PaginationNext
          disabled={current === pagesCount}
          onClick={() => handlePageChange(isNaN(current) ? 1 : current + 1)}
        />
      </PaginationContent>
    </Pagination>
  );
};
export default PropsPagination;
